import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  InputAdornment
} from '@mui/material';
import {
  Send as SendIcon,
  Preview as PreviewIcon,
  NotificationsActive as NotificationIcon,
  Group as GroupIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Search
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { notificationsAPI, flatsAPI, usersAPI } from '../services/api.js';
import { useQuery, useMutation, useQueryClient } from 'react-query';

const MassNotifications = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for building associations
  const [buildingIds, setBuildingIds] = useState([]);
  const [buildingName, setBuildingName] = useState('');
  const [isLoadingAssociations, setIsLoadingAssociations] = useState(true);
  
  // State management
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [targetAudience, setTargetAudience] = useState('ALL');
  const [specificFlats, setSpecificFlats] = useState([]);
  const [templateVariables, setTemplateVariables] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [expandedTemplate, setExpandedTemplate] = useState(false);
  
  // Flat search state
  const [flatSearchTerm, setFlatSearchTerm] = useState('');

  const buildingId = buildingIds[0]; // For single building admins

  // Fetch building associations for the user
  useEffect(() => {
    const fetchAssociations = async () => {
      if (!user) {
        setIsLoadingAssociations(false);
        return;
      }
      const userId = user.userId || user.attributes?.sub || user.username;
      try {
        const res = await usersAPI.getAssociations(userId);
        // Collect buildingIds and building name from association results
        const buildingAssociations = res.data || [];
        
        const ids = buildingAssociations.map(item => item.buildingId);
        setBuildingIds(ids);
        
        // Set building name from the first building association
        if (buildingAssociations.length > 0) {
          const firstBuilding = buildingAssociations[0];
          setBuildingName(firstBuilding.buildingName || 'Building');
        }
      } catch (error) {
        console.error('Error fetching user associations:', error);
      } finally {
        setIsLoadingAssociations(false);
      }
    };

    fetchAssociations();
  }, [user]);

  // Fetch notification templates
  const { data: templatesData, isLoading: templatesLoading } = useQuery(
    'notificationTemplates',
    () => notificationsAPI.getTemplates(),
    {
      select: (response) => response.data?.templates || {},
      onError: (err) => setError('Failed to load notification templates')
    }
  );

  // Fetch flats for the building (for specific flat targeting)
  const { data: flats = [], isLoading: flatsLoading } = useQuery(
    ['flats', buildingId],
    () => buildingId ? flatsAPI.getByBuilding(buildingId) : Promise.resolve({ data: [] }),
    {
      select: (response) => {
        console.log('Flats API response:', response);
        return response.data || [];
      },
      enabled: !!buildingId
    }
  );

  // Debug flats data
  React.useEffect(() => {
    console.log('Flats loaded:', flats);
    console.log('Flats loading:', flatsLoading);
    console.log('Building ID:', buildingId);
  }, [flats, flatsLoading, buildingId]);

  // Send mass notification mutation
  const sendNotificationMutation = useMutation(
    (notificationData) => notificationsAPI.sendMassNotification(notificationData),
    {
      onSuccess: (response) => {
        const data = response.data;
        setSuccess(t('notifications.massNotificationSent', { count: data.notificationsSent }));
        resetForm();
        queryClient.invalidateQueries('notifications');
      },
      onError: (err) => {
        setError(err.response?.data?.message || t('notifications.sendFailed'));
      }
    }
  );

  // Template categories for organization
  const templateCategories = {
    MAINTENANCE: 'Maintenance & Repairs',
    UTILITY: 'Utilities',
    FINANCIAL: 'Payments & Finances',
    SECURITY: 'Security & Safety',
    COMMUNITY: 'Community Events',
    POLICY: 'Policies & Rules',
    FACILITY: 'Facilities & Amenities',
    EMERGENCY: 'Emergency Alerts',
    GENERAL: 'General Announcements'
  };

  // Priority colors
  const priorityColors = {
    LOW: 'success',
    MEDIUM: 'warning',
    HIGH: 'error',
    URGENT: 'error'
  };

  // Target audience options
  const audienceOptions = [
    { value: 'ALL', label: 'All Residents', icon: <GroupIcon />, description: 'Send to all users in the building' },
    { value: 'OWNERS', label: 'Property Owners Only', icon: <HomeIcon />, description: 'Send only to flat owners' },
    { value: 'TENANTS', label: 'Tenants Only', icon: <PersonIcon />, description: 'Send only to tenants' },
    { value: 'OWNERS_TENANTS', label: 'Owners & Tenants', icon: <GroupIcon />, description: 'Send to both owners and tenants' },
    { value: 'SPECIFIC_FLATS', label: 'Specific Flats', icon: <HomeIcon />, description: 'Send to selected flats only' }
  ];

  // Reset form
  const resetForm = () => {
    setSelectedTemplate('');
    setCustomTitle('');
    setCustomMessage('');
    setTargetAudience('ALL');
    setSpecificFlats([]);
    setTemplateVariables({});
  };

  // Handle template selection
  const handleTemplateChange = (templateType) => {
    setSelectedTemplate(templateType);
    if (templateType && templatesData[templateType]) {
      // Initialize template variables based on the template
      const template = templatesData[templateType];
      const variableMatches = template.template.message.match(/\{\{([^}]+)\}\}/g) || [];
      const titleMatches = template.template.title.match(/\{\{([^}]+)\}\}/g) || [];
      
      const allMatches = [...variableMatches, ...titleMatches];
      const variables = {};
      
      allMatches.forEach(match => {
        const variable = match.replace(/[{}]/g, '');
        if (!variables[variable]) {
          variables[variable] = '';
        }
      });
      
      setTemplateVariables(variables);
    } else {
      setTemplateVariables({});
    }
  };

  // Handle variable change
  const handleVariableChange = (variable, value) => {
    setTemplateVariables(prev => ({
      ...prev,
      [variable]: value
    }));
  };

  // Handle flat selection for specific targeting
  const handleFlatToggle = (flatId) => {
    console.log('Toggling flat:', flatId);
    console.log('Current specificFlats:', specificFlats);
    setSpecificFlats(prev => {
      const newSelection = prev.includes(flatId)
        ? prev.filter(id => id !== flatId)
        : [...prev, flatId];
      console.log('New specificFlats:', newSelection);
      return newSelection;
    });
  };

  // Generate preview content
  const generatePreview = () => {
    if (selectedTemplate && templatesData[selectedTemplate]) {
      const template = templatesData[selectedTemplate];
      let title = template.template.title;
      let message = template.template.message;
      
      // Replace variables
      Object.entries(templateVariables).forEach(([variable, value]) => {
        const regex = new RegExp(`\\{\\{${variable}\\}\\}`, 'g');
        title = title.replace(regex, value || `[${variable}]`);
        message = message.replace(regex, value || `[${variable}]`);
      });
      
      return { title, message, category: template.category, priority: template.priority };
    } else {
      return { title: customTitle, message: customMessage, category: 'GENERAL', priority: 'MEDIUM' };
    }
  };

  // Handle send notification
  const handleSend = () => {
    if (!buildingId) {
      setError('Building ID is required');
      return;
    }

    if (targetAudience === 'SPECIFIC_FLATS' && specificFlats.length === 0) {
      setError('Please select at least one flat for specific targeting');
      return;
    }

    if (!selectedTemplate && (!customTitle || !customMessage)) {
      setError(t('notifications.templateRequired'));
      return;
    }

    const notificationData = {
      buildingId,
      templateType: selectedTemplate || undefined,
      customTitle: selectedTemplate ? undefined : customTitle,
      customMessage: selectedTemplate ? undefined : customMessage,
      variables: templateVariables,
      targetAudience,
      specificFlats: targetAudience === 'SPECIFIC_FLATS' ? specificFlats : [],
      priority: selectedTemplate && templatesData[selectedTemplate] ? templatesData[selectedTemplate].priority : 'MEDIUM'
    };

    sendNotificationMutation.mutate(notificationData);
  };

  // Group templates by category
  const groupedTemplates = Object.entries(templatesData || {}).reduce((acc, [key, template]) => {
    const category = template.category || 'GENERAL';
    if (!acc[category]) acc[category] = [];
    acc[category].push({ key, ...template });
    return acc;
  }, {});

  // Show loading while fetching associations
  if (isLoadingAssociations) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading building information...</Typography>
      </Box>
    );
  }

  if (!buildingId) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="error">
            No building assigned. Please contact administrator.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Typography 
        variant={isMobile ? "h5" : "h4"} 
        sx={{ 
          mb: isMobile ? 2 : 3, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          flexDirection: isMobile ? 'column' : 'row',
          textAlign: isMobile ? 'center' : 'left'
        }}
      >
        <NotificationIcon />
        {t('notifications.massNotification')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Template Selection */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: isMobile ? 2 : 0 }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ mb: 2, fontWeight: 'bold' }}>
                {t('notifications.step1Template')}
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('notifications.notificationTemplate')}</InputLabel>
                <Select
                  value={selectedTemplate}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  label="Notification Template"
                  size={isMobile ? "small" : "medium"}
                >
                  <MenuItem value="">
                    <em>{t('notifications.customMessage')}</em>
                  </MenuItem>
                  {Object.entries(groupedTemplates).map(([category, templates]) => (
                    <MenuItem key={category} disabled>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {templateCategories[category]}
                      </Typography>
                    </MenuItem>
                  ))}
                  {Object.entries(groupedTemplates).map(([category, templates]) => 
                    templates.map((template) => (
                      <MenuItem key={template.key} value={template.key} sx={{ pl: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: isMobile ? 'flex-start' : 'center'
                        }}>
                          <Chip 
                            size="small" 
                            label={template.priority} 
                            color={priorityColors[template.priority]} 
                          />
                          <Typography variant={isMobile ? "body2" : "body1"}>
                            {template.template.title}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {/* Template Preview */}
              {selectedTemplate && templatesData[selectedTemplate] && (
                <Accordion 
                  expanded={expandedTemplate} 
                  onChange={() => setExpandedTemplate(!expandedTemplate)}
                  sx={{ boxShadow: isMobile ? 1 : 2 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant={isMobile ? "body1" : "subtitle1"}>
                      Template Preview & Variables
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: isMobile ? 1 : 2 }}>
                    <Box>
                      <Typography variant={isMobile ? "body2" : "subtitle2"} gutterBottom sx={{ fontWeight: 'bold' }}>
                        Template: {templatesData[selectedTemplate].template.title}
                      </Typography>
                      <Typography 
                        variant={isMobile ? "caption" : "body2"} 
                        color="text.secondary" 
                        sx={{ mb: 2 }}
                      >
                        {templatesData[selectedTemplate].template.message}
                      </Typography>

                      {/* Template Variables */}
                      {Object.keys(templateVariables).length > 0 && (
                        <Box>
                          <Typography variant={isMobile ? "body2" : "subtitle2"} sx={{ mb: 1, fontWeight: 'bold' }}>
                            Fill in the details:
                          </Typography>
                          <Grid container spacing={2}>
                            {Object.entries(templateVariables).map(([variable, value]) => (
                              <Grid item xs={12} sm={isMobile ? 12 : 6} key={variable}>
                                <TextField
                                  fullWidth
                                  label={variable.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                  value={value}
                                  onChange={(e) => handleVariableChange(variable, e.target.value)}
                                  size={isMobile ? "small" : "medium"}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Custom Message Fields */}
              {!selectedTemplate && (
                <Box>
                  <TextField
                    fullWidth
                    label={t('notifications.subject')}
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label={t('notifications.message')}
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    multiline
                    rows={isMobile ? 3 : 4}
                    sx={{ mb: 2 }}
                    size={isMobile ? "small" : "medium"}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Target Audience Selection */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: isMobile ? 2 : 0 }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ mb: 2, fontWeight: 'bold' }}>
                {t('notifications.step2Recipients')}
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('notifications.recipients')}</InputLabel>
                <Select
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  label="Target Audience"
                  size={isMobile ? "small" : "medium"}
                >
                  {audienceOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'flex-start' : 'center'
                      }}>
                        {option.icon}
                        <Box>
                          <Typography variant={isMobile ? "body2" : "body1"}>
                            {option.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Specific Flats Selection */}
              {targetAudience === 'SPECIFIC_FLATS' && (
                <Box>
                  <Typography variant={isMobile ? "body2" : "subtitle2"} sx={{ mb: 1, fontWeight: 'bold' }}>
                    Select Flats ({specificFlats.length} selected):
                  </Typography>
                  
                  {/* Flat search field */}
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search flats by number, floor, or type..."
                    value={flatSearchTerm}
                    onChange={(e) => setFlatSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                  
                  <Paper sx={{ maxHeight: isMobile ? 150 : 200, overflow: 'auto', p: 1 }}>
                    <List dense>
                      {flatsLoading ? (
                        <ListItem>
                          <CircularProgress size={20} />
                          <ListItemText primary="Loading flats..." />
                        </ListItem>
                      ) : flats.length === 0 ? (
                        <ListItem>
                          <ListItemText primary="No flats available for this building" />
                        </ListItem>
                      ) : (
                        (() => {
                          // Filter flats based on search term
                          const filteredFlats = flats.filter(flat => {
                            if (!flatSearchTerm) return true;
                            const searchLower = flatSearchTerm.toLowerCase();
                            return (
                              (flat.flatNumber && flat.flatNumber.toString().toLowerCase().includes(searchLower)) ||
                              (flat.floorNumber && flat.floorNumber.toString().toLowerCase().includes(searchLower)) ||
                              (flat.flatType && flat.flatType.toLowerCase().includes(searchLower))
                            );
                          });
                          
                          if (filteredFlats.length === 0) {
                            return (
                              <ListItem>
                                <ListItemText primary="No flats match your search" />
                              </ListItem>
                            );
                          }
                          
                          return filteredFlats.map((flat) => (
                            <ListItem 
                              key={flat.flatId} 
                              dense 
                              button
                              onClick={() => handleFlatToggle(flat.flatId)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                                backgroundColor: specificFlats.includes(flat.flatId) ? 'action.selected' : 'transparent'
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={specificFlats.includes(flat.flatId)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleFlatToggle(flat.flatId);
                                    }}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="body2">
                                      Flat {flat.flatNumber}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Floor {flat.floorNumber} • {flat.flatType}
                                    </Typography>
                                  </Box>
                                }
                                sx={{ width: '100%', margin: 0 }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </ListItem>
                          ));
                        })()
                      )}
                    </List>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Card sx={{ mb: isMobile ? 2 : 0 }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ mb: 2, fontWeight: 'bold' }}>
                {t('notifications.step3ReviewSend')}
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                gap: isMobile ? 1 : 2, 
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={() => setPreviewOpen(true)}
                  disabled={!selectedTemplate && (!customTitle || !customMessage)}
                  fullWidth={isMobile}
                  sx={{ minHeight: isMobile ? 44 : 'auto' }}
                >
                  Preview
                </Button>

                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleSend}
                  disabled={
                    sendNotificationMutation.isLoading || 
                    (!selectedTemplate && (!customTitle || !customMessage)) ||
                    (targetAudience === 'SPECIFIC_FLATS' && specificFlats.length === 0)
                  }
                  fullWidth={isMobile}
                  sx={{ minHeight: isMobile ? 44 : 'auto' }}
                >
                  {sendNotificationMutation.isLoading ? t('notifications.sending') : t('notifications.sendNotification')}
                </Button>

                <Button
                  variant="text"
                  onClick={resetForm}
                  fullWidth={isMobile}
                  sx={{ minHeight: isMobile ? 44 : 'auto' }}
                >
                  Reset
                </Button>
              </Box>

              {/* Quick Stats */}
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                flexWrap: 'wrap',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <Chip 
                  label={`Building ID: ${buildingId}`} 
                  color="primary" 
                  variant="outlined" 
                  size={isMobile ? "small" : "medium"}
                  sx={{ alignSelf: isMobile ? 'flex-start' : 'auto' }}
                />
                <Chip 
                  label={`Recipients: ${audienceOptions.find(o => o.value === targetAudience)?.label}`} 
                  color="secondary" 
                  variant="outlined" 
                  size={isMobile ? "small" : "medium"}
                  sx={{ alignSelf: isMobile ? 'flex-start' : 'auto' }}
                />
                {targetAudience === 'SPECIFIC_FLATS' && (
                  <Chip 
                    label={`${specificFlats.length} flats selected`} 
                    color="info" 
                    variant="outlined" 
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={() => setPreviewOpen(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          position: isMobile ? 'sticky' : 'static', 
          top: 0, 
          bgcolor: 'background.paper', 
          zIndex: 1 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PreviewIcon />
            <Typography variant={isMobile ? "h6" : "h5"}>
              Notification Preview
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          {(() => {
            const preview = generatePreview();
            return (
              <Box>
                <Paper sx={{ 
                  p: isMobile ? 2 : 3, 
                  mb: 2, 
                  bgcolor: 'grey.50',
                  boxShadow: isMobile ? 1 : 2
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 1,
                    flexWrap: 'wrap'
                  }}>
                    <Chip 
                      size="small" 
                      label={preview.priority} 
                      color={priorityColors[preview.priority]} 
                    />
                    <Chip 
                      size="small" 
                      label={preview.category} 
                      variant="outlined" 
                    />
                  </Box>
                  <Typography variant={isMobile ? "body1" : "h6"} sx={{ mb: 1, fontWeight: 'bold' }}>
                    {preview.title}
                  </Typography>
                  <Typography variant={isMobile ? "body2" : "body1"}>
                    {preview.message}
                  </Typography>
                </Paper>

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant={isMobile ? "caption" : "body2"}>
                    This notification will be sent to: <strong>
                      {audienceOptions.find(o => o.value === targetAudience)?.label}
                    </strong>
                    {targetAudience === 'SPECIFIC_FLATS' && ` (${specificFlats.length} flats)`}
                  </Typography>
                </Alert>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ 
          position: isMobile ? 'sticky' : 'static', 
          bottom: 0, 
          bgcolor: 'background.paper', 
          p: 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0
        }}>
          <Button 
            onClick={() => setPreviewOpen(false)}
            fullWidth={isMobile}
            sx={{ minHeight: isMobile ? 44 : 'auto' }}
          >
            Close
          </Button>
          <Button 
            variant="contained" 
            startIcon={<SendIcon />}
            onClick={() => {
              setPreviewOpen(false);
              handleSend();
            }}
            disabled={sendNotificationMutation.isLoading}
            fullWidth={isMobile}
            sx={{ minHeight: isMobile ? 44 : 'auto' }}
          >
            Send Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MassNotifications;